import { supabase } from '../../supabaseClient';
import { get, post, postFormData } from './core';
import type {
  FidePairingsCheckResult,
  FideExportReadinessResponse,
  FidePlayerLookupResponse,
  FideTrf16ImportResult,
  FideListStatus,
  FideImportProgress,
} from '@swiss-magic/shared-types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Story 097 — FIDE TRF16 export.

export function getTrf16Readiness(
  tournamentId: string,
): Promise<FideExportReadinessResponse> {
  return get(`/tournaments/${tournamentId}/trf16/readiness`);
}

// Story 098 — FIDE ID autofill.

/** Look up a FIDE ID in the local rating-list mirror. Throws ApiError (404)
 *  when the ID isn't present. */
export function lookupFidePlayer(
  fideId: number | string,
): Promise<FidePlayerLookupResponse> {
  return get(`/fide/players/${fideId}`);
}

/** Admin: current state of the local FIDE rating-list mirror. */
export function getFideListStatus(): Promise<FideListStatus> {
  return get('/admin/fide/status');
}

/** Admin: upload a FIDE rating-list dump (players_list_xml.zip or .xml).
 *  Returns as soon as the upload lands; the ingest runs as a background job —
 *  poll getFideImportProgress(). The ~70 MB transfer itself can take a while
 *  on a normal uplink, so this overrides the default 15s request timeout. */
export function importFideList(file: File): Promise<{ started: boolean }> {
  const form = new FormData();
  form.append('file', file);
  return postFormData('/admin/fide/import', form, { timeoutMs: 10 * 60_000 });
}

/** Admin: progress of the current/last background rating-list import. */
export function getFideImportProgress(): Promise<FideImportProgress> {
  return get('/admin/fide/import/progress');
}

/** Story 099 — import a TRF16 file into a new draft tournament. */
export function importTrf16(file: File): Promise<FideTrf16ImportResult> {
  const form = new FormData();
  form.append('file', file);
  return postFormData('/fide/import-trf16', form);
}

/** Fetch the TRF16 text (authed) and trigger a browser download. The core
 *  `get`/`post` helpers return parsed JSON, so we hand-roll the blob fetch
 *  here — mirroring PrintingCenterPage's PDF download, but with the auth +
 *  tenant headers the core client normally adds. */
export async function downloadTrf16(tournamentId: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/tournaments/${tournamentId}/trf16`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Swiss-Tenant-Host': window.location.hostname,
    },
  });
  if (!res.ok) throw new Error('Failed to generate TRF16');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download =
    res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] ??
    'trf16.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Story 101 — PUBLIC pairings checker (no auth: the page is unauthenticated).
export function checkPairings(file: File): Promise<FidePairingsCheckResult> {
  const form = new FormData();
  form.append('file', file);
  return postFormData('/fide/public/check-pairings', form, {
    auth: false,
    timeoutMs: 60_000,
  });
}

// Story 102 — PUBLIC random tournament generator (no auth).
export interface GenerateTournamentRequest {
  players?: number;
  rounds?: number;
  drawPercentage?: number;
  highestRating?: number;
  lowestRating?: number;
  seed?: number;
}

export function generateTournament(
  params: GenerateTournamentRequest,
): Promise<{ trf: string; seed: number }> {
  return post('/fide/public/generate-tournament', params, { auth: false });
}
